import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './api';

export type NenchoStatus = 'open' | 'collecting' | 'computing' | 'closed';

export interface NenchoCycle {
  id: string;
  year: number;
  status: NenchoStatus;
  collectionDeadline: string | null;
  closedAt: string | null;
  createdAt: string;
}

export interface NenchoInput {
  id: string;
  employeeId: string;
  spouseDependent: boolean;
  childDependents: number;
  otherDependents: number;
  lifeInsuranceGeneralCents: number;
  lifeInsurancePensionCents: number;
  lifeInsuranceCareCents: number;
  earthquakeInsuranceCents: number;
  socialInsuranceOtherCents: number;
  idecoCents: number;
  mortgageCreditCents: number;
  // Phase 2B deductions (optional)
  spouseAnnualIncomeCents?: number | null;
  disabilityGeneralCount?: number;
  disabilitySpecialCount?: number;
  disabilityCohabitingSpecialCount?: number;
  isSingleParent?: boolean;
  isWidow?: boolean;
  isWorkingStudent?: boolean;
  notes: string | null;
  submittedAt: string | null;
}

export interface NenchoResult {
  id: string;
  employeeId: string;
  annualGrossCents: number;
  annualEmploymentDeductionCents: number;
  totalDeductionsCents: number;
  taxableIncomeCents: number;
  computedAnnualTaxCents: number;
  withheldTotalCents: number;
  adjustmentCents: number;
  breakdown: Record<string, number>;
  computedAt: string;
}

export function useNenchoCycles() {
  return useQuery({
    queryKey: ['nencho-cycles'],
    queryFn: async () => {
      const r = await api.get<{ cycles: NenchoCycle[] }>('/api/nencho/cycles');
      return r.data.cycles;
    },
  });
}

export function useOpenNenchoCycle() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { year: number; collectionDeadline?: string }) => {
      const r = await api.post<NenchoCycle>('/api/nencho/cycles', args);
      return r.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['nencho-cycles'] }),
  });
}

/** Drive the 年末調整 cycle lifecycle: open → collecting → computing → closed. */
export function useSetNenchoCycleStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { id: string; status: NenchoStatus }) => {
      const r = await api.post<NenchoCycle>(`/api/nencho/cycles/${args.id}/status`, { status: args.status });
      return r.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['nencho-cycles'] }),
  });
}

export function useNenchoInputs(cycleId: string | null) {
  return useQuery({
    queryKey: ['nencho-inputs', cycleId],
    enabled: !!cycleId,
    queryFn: async () => {
      const r = await api.get<{ inputs: NenchoInput[] }>(`/api/nencho/cycles/${cycleId}/inputs`);
      return r.data.inputs;
    },
  });
}

/** Carry-forward: the employee's most recent declaration from a prior cycle (or null). */
export function useLastNenchoInput(cycleId: string | null, employeeId: string | null, enabled: boolean) {
  return useQuery({
    queryKey: ['nencho-last-input', cycleId, employeeId],
    enabled: enabled && !!cycleId && !!employeeId,
    queryFn: async () => {
      const r = await api.get<{ input: NenchoInput | null }>(`/api/nencho/cycles/${cycleId}/employees/${employeeId}/last-input`);
      return r.data.input;
    },
  });
}

export function useSubmitNenchoInput() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { cycleId: string; payload: Omit<NenchoInput, 'id' | 'submittedAt' | 'notes'> & { notes?: string } }) => {
      const r = await api.post<NenchoInput>(`/api/nencho/cycles/${args.cycleId}/inputs`, args.payload);
      return r.data;
    },
    onSuccess: (_d, vars) => qc.invalidateQueries({ queryKey: ['nencho-inputs', vars.cycleId] }),
  });
}

export interface ComputeNenchoResponse {
  results: NenchoResult[];
  submittedInputCount: number;
  employeeCount: number;
}

export function useComputeNencho() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (cycleId: string) => {
      const r = await api.post<ComputeNenchoResponse>(`/api/nencho/cycles/${cycleId}/compute`);
      return r.data;
    },
    onSuccess: (_d, cycleId) => {
      qc.invalidateQueries({ queryKey: ['nencho-results', cycleId] });
      qc.invalidateQueries({ queryKey: ['nencho-cycles'] });
      qc.invalidateQueries({ queryKey: ['nencho-inputs', cycleId] });
    },
  });
}

/** Authenticated (cookie) URL for an employee's 源泉徴収票 PDF. Open in a new tab. */
export function gensenPdfUrl(cycleId: string, employeeId: string): string {
  const base = (import.meta.env.VITE_API_URL as string | undefined) || 'http://localhost:3001';
  return `${base}/api/nencho/cycles/${cycleId}/employees/${employeeId}/gensen.pdf`;
}

/** Authenticated (cookie) URL for the cycle's 法定調書合計表 (employer summary) PDF. */
export function hoteiChoshoPdfUrl(cycleId: string): string {
  const base = (import.meta.env.VITE_API_URL as string | undefined) || 'http://localhost:3001';
  return `${base}/api/nencho/cycles/${cycleId}/hotei-chosho.pdf`;
}

export function useNenchoResults(cycleId: string | null) {
  return useQuery({
    queryKey: ['nencho-results', cycleId],
    enabled: !!cycleId,
    queryFn: async () => {
      const r = await api.get<{ results: NenchoResult[] }>(`/api/nencho/cycles/${cycleId}/results`);
      return r.data.results;
    },
  });
}
